import * as React from "react";
import {
  Button,
  CssBaseline,
  ImageList,
  ImageListItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import SlowMotionVideoIcon from "@mui/icons-material/SlowMotionVideo";
import EditIcon from "@mui/icons-material/Edit";
import {
  getCarouselMedia,
  getCarouselsError,
  getCarouselsStatus,
  selectAllCarousels,
} from "../../../features/carouselSlice";
import { useDispatch, useSelector } from "react-redux";
import Loading from "../../../components/loading";
import Error from "../../../components/Error";
import { useRestaurant } from "../../../hooks/RestaurantContext";
import PreviewModal from "../../../components/previewModal";
import MediaPreviewDialog from "../../../components/mediaPreviewDialog";
import AppBarSearch from "../../../global/appBarSearch";
import { ResponsiveProvider } from "../../../hooks/uiHook";
import { useTranslation } from "react-i18next";
const CarouselMedia = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { restaurantId } = useRestaurant();
  const [openModal, setOpenModal] = React.useState(false);
  const [preview, setPreview] = React.useState(null);
  const status = useSelector(getCarouselsStatus);
  const error = useSelector(getCarouselsError);
  const carouselItems = useSelector(selectAllCarousels);
  const apiUrl = process.env.REACT_APP_MEDIA_URL;

  const hasFetchedInMountRef = React.useRef(false);
  const lastFetchedRestaurantRef = React.useRef(restaurantId);
  React.useEffect(() => {
    const restaurantChanged =
      hasFetchedInMountRef.current &&
      lastFetchedRestaurantRef.current !== restaurantId;
    const alreadyLoaded = carouselItems.length > 0 && status === "fetchData";
    if (restaurantChanged || !alreadyLoaded) {
      dispatch(getCarouselMedia(restaurantId));
    }
    hasFetchedInMountRef.current = true;
    lastFetchedRestaurantRef.current = restaurantId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, restaurantId]);

  return (
    <div className="main-application">
      <CssBaseline />
      <ResponsiveProvider maxWidth={900}>
        <AppBarSearch
          searchText={t("search.byNameOrDescription")}
          title={t("pages.myCarousel")}
          actions={
            <>
              <Button
                variant="contained"
                color="info"
                startIcon={<EditIcon />}
                onClick={() => navigate("modify")}
              >
                {t("common.edit")}
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<AddIcon />}
                onClick={() => navigate("add")}
              >
                {t("actions.addMedia")}
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<SlowMotionVideoIcon />}
                onClick={() => setOpenModal(true)}
              >
                {t("actions.demonstration")}
              </Button>
            </>
          }
        />
      </ResponsiveProvider>
      <main>
        <ImageList variant="masonry" cols={3} gap={8}>
          {status === "loadingCarousel" ? (
            <Loading />
          ) : status === "fetchError" ? (
            <Error>{error}</Error>
          ) : (
            <>
              {carouselItems &&
                carouselItems.map((item, index) => (
                  <ImageListItem key={item._id ?? index}>
                    {item.mediaType === "image" ? (
                      <img
                        src={`${apiUrl}/${item.fileUrl}?w=248&fit=crop&auto=format`}
                        srcSet={`
                          ${apiUrl}/${item.fileUrl}?w=248&fit=crop&auto=format 248w,
                          ${apiUrl}/${item.fileUrl}?w=496&fit=crop&auto=format 496w
                        `}
                        sizes="(max-width: 600px) 248px, 496px"
                        alt={`carousel-item-${index}`}
                        loading="lazy"
                        onClick={() =>
                          setPreview({
                            ...item,
                            src: `${apiUrl}/${item.fileUrl}`,
                            video: false,
                          })
                        }
                        style={{ cursor: "pointer" }}
                      />
                    ) : (
                      <video
                        src={`${apiUrl}/${item.fileUrl}`}
                        controls
                        onClick={() =>
                          setPreview({
                            ...item,
                            src: `${apiUrl}/${item.fileUrl}`,
                            video: true,
                          })
                        }
                        style={{ cursor: "pointer", width: "100%" }}
                      />
                    )}
                  </ImageListItem>
                ))}
            </>
          )}
        </ImageList>
        {openModal && (
          <PreviewModal
            open={openModal}
            handleClose={() => setOpenModal(false)}
          />
        )}
        <MediaPreviewDialog
          preview={preview}
          onClose={() => setPreview(null)}
          canRename={false}
        />
      </main>
    </div>
  );
};

export default CarouselMedia;
